/**
 * Detect Self-Scan mode — dedicated route or `?selfScan=1` on scanner.
 * When true, server SelfScanBasket is SoT (never useShopCart).
 */

const SELF_SCAN_PATH_SEGMENT = '/self-scan';

export function isSelfScanRoutePath(pathname: string, tenantCode: string): boolean {
  const prefix = `/${tenantCode}`;
  if (pathname === `${prefix}${SELF_SCAN_PATH_SEGMENT}`) {
    return true;
  }
  return pathname.startsWith(`${prefix}${SELF_SCAN_PATH_SEGMENT}/`);
}

export function isSelfScanQueryEnabled(search: string): boolean {
  const query = search.startsWith('?') ? search.slice(1) : search;
  if (query.length === 0) {
    return false;
  }
  const params = new URLSearchParams(query);
  const flag = params.get('selfScan');
  return flag === '1' || flag === 'true';
}

export function isSelfScanMode(input: {
  readonly pathname: string;
  readonly search: string;
  readonly tenantCode: string;
}): boolean {
  return (
    isSelfScanRoutePath(input.pathname, input.tenantCode) ||
    isSelfScanQueryEnabled(input.search)
  );
}

/**
 * Checkout handoff parks `selfScan=1` (+ optional `basketPublicId`) so currency-switch /
 * remount keep Self-Scan context. `basketPublicId` alone still signals Self-Scan bind
 * when `selfScan=1` is stripped. Durable currency lock also uses session `collectLockedAt`
 * when search is `?sessionId=…` only (G1 / AC-11).
 * CASH Stage 1–4 remains offered when readiness allows — customer never self-confirms
 * (staff ConfirmCashPaymentReceivedUseCase).
 */
export function isSelfScanCheckoutSearch(search: string): boolean {
  if (isSelfScanQueryEnabled(search)) {
    return true;
  }
  return resolveSelfScanBasketPublicId(search) !== null;
}

export function resolveSelfScanSalesPointId(search: string): number | null {
  const query = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(query);
  for (const key of ['salesPointId', 'kioskId'] as const) {
    const raw = params.get(key);
    if (raw === null || raw.length === 0) {
      continue;
    }
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) {
      return Math.trunc(n);
    }
  }
  return null;
}

/**
 * Basket public id from query (`basketPublicId` allowlisted) or path segment.
 */
export function resolveSelfScanBasketPublicId(
  search: string,
  pathnameBasketId?: string | null,
): string | null {
  if (
    pathnameBasketId !== undefined &&
    pathnameBasketId !== null &&
    pathnameBasketId.trim().length > 0
  ) {
    return pathnameBasketId.trim();
  }
  const query = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(query);
  for (const key of ['basketPublicId', 'basket'] as const) {
    const basket = params.get(key)?.trim() ?? '';
    if (basket.length > 0) {
      return basket;
    }
  }
  return null;
}
